import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, resolveComponent as _resolveComponent, createVNode as _createVNode, Transition as _Transition, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "text-center p-8"
}
const _hoisted_2 = { class: "bg-gradient-to-br from-blue-500 to-gray-600 text-white p-8 rounded-2xl shadow-lg" }
const _hoisted_3 = { class: "text-3xl font-bold mb-4" }
const _hoisted_4 = { class: "text-xl mb-4" }
const _hoisted_5 = { class: "text-4xl font-bold text-yellow-300" }
const _hoisted_6 = { key: 1 }
const _hoisted_7 = { class: "p-6 rounded-2xl shadow-sm bg-white animate-fade-in transition-all duration-500" }
const _hoisted_8 = { class: "flex w-full items-end justify-end text-sm text-gray-500 space-x-2" }
const _hoisted_9 = { class: "text-blue-700 text-sm" }
const _hoisted_10 = { class: "relative w-12 h-12" }
const _hoisted_11 = { class: "w-full h-full transform -rotate-90" }
const _hoisted_12 = ["stroke-dashoffset"]
const _hoisted_13 = { class: "absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 font-bold text-red-500 text-xl" }
const _hoisted_14 = { class: "text-xl mb-1 font-bold text-gray-800 tracking-wide" }
const _hoisted_15 = { class: "space-y-3" }
const _hoisted_16 = ["disabled", "onClick"]
const _hoisted_17 = { class: "mb-2 px-2 mt-1" }
const _hoisted_18 = { class: "text-blue-500 text-left py-1 rounded-full text-sm font-medium shadow-sm" }
const _hoisted_19 = { class: "w-full h-2 bg-gray-200 rounded-full overflow-hidden" }
const _hoisted_20 = {
  key: 0,
  class: "mt-6 text-right"
}
const _hoisted_21 = {
  key: 1,
  class: "mt-6 border-t pt-4"
}
const _hoisted_22 = {
  key: 0,
  class: "space-y-2"
}
const _hoisted_23 = {
  key: 1,
  class: "space-y-2"
}
const _hoisted_24 = { class: "p-2 rounded-lg" }
const _hoisted_25 = { class: "flex flex-wrap gap-2" }
const _hoisted_26 = {
  key: 0,
  class: "text-gray-700"
}
const _hoisted_27 = ["onClick"]
const _hoisted_28 = { class: "text-lg font-semibold text-blue-900 mb-2" }
const _hoisted_29 = { class: "space-y-3" }
const _hoisted_30 = { class: "text-gray-700" }
const _hoisted_31 = {
  key: 0,
  class: "text-gray-600 italic mt-1"
}
const _hoisted_32 = {
  key: 2,
  class: "text-center text-gray-400 mt-12 animate-fade-in"
}

import { ref, computed, onMounted, onUnmounted } from "vue";
import LearnEnglishPagePatternLayout from "../LearnEnglishPagePatternLayout.vue";
import { IVocabulary } from "@/models/IIearnEnglish";
import { practiceVoca } from "@/services/learn/practiceService";
import { shuffle, upperCase } from "lodash";
import { SoundOutlined } from "@ant-design/icons-vue";
import { getVocabularyByDictionary } from "@/api/dictionaryApi";
import { IDictionaryEntry } from "@/models/IDictionaryEntry";
import i18n from "@/services/i18n";
import correctSound from "@/assets/audio/correct-6033.mp3";
import wrongSound from "@/assets/audio/error-5-199276.mp3";

const STORAGE_KEY = "selected_vocabularies";

export default /*@__PURE__*/_defineComponent({
  __name: 'practiceVoca-page',
  setup(__props) {

const questions = ref(practiceVoca(shuffle(getStoredVocabularies())));
const currentIndex = ref(0);
const selectedAnswer = ref<string | null>(null);
const score = ref(0);
const timeLeft = ref(10);
const timer = ref<number | null>(null);
const isTimeUp = ref(false);
const dictionaryData = ref<IDictionaryEntry | null>(null);
const isLoading = ref(false);
const nextQuestionCountdown = ref(10);
const nextQuestionTimer = ref<number | null>(null);
const isBreakTime = ref(false);
const breakTimeLeft = ref(5);
const breakTimer = ref<number | null>(null);

function getStoredVocabularies(): IVocabulary[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

const currentQuestion = computed(() => questions.value[currentIndex.value]);
const isLastQuestion = computed(
  () => currentIndex.value === questions.value.length - 1
);

const playAudio = (audioUrl: string) => {
  const audio = new Audio(audioUrl);
  audio.play();
};

const getInfo = async (word: string) => {
  try {
    isLoading.value = true;
    const response = await getVocabularyByDictionary(word);
    if (response && response.length > 0) {
      dictionaryData.value = response[0];
      // Tự động phát âm thanh đầu tiên nếu có
      const firstAudio = dictionaryData.value.phonetics.find((p) => p.audio);
      if (firstAudio?.audio) {
        playAudio(firstAudio.audio);
      }
    }
  } catch (error) {
    dictionaryData.value = null;
  } finally {
    isLoading.value = false;
  }
};

const playResultSound = (isCorrect: boolean) => {
  const audio = new Audio(isCorrect ? correctSound : wrongSound);
  audio.play();
};

const selectAnswer = (option: string) => {
  if (selectedAnswer.value || isTimeUp.value) return;
  selectedAnswer.value = option;
  const isCorrect = option === currentQuestion.value.correctAnswer;
  if (isCorrect) {
    // Tính điểm dựa trên thời gian còn lại
    // Thời gian càng nhiều điểm càng cao, tối đa 1000 điểm
    const maxTime = 10; // Thời gian tối đa cho mỗi câu
    const maxScore = 1000; // Điểm tối đa cho mỗi câu
    const timeBonus = Math.floor((timeLeft.value / maxTime) * maxScore);
    score.value += timeBonus;
  }
  // Phát âm thanh kết quả
  playResultSound(isCorrect);
  stopTimer();
  // Gọi API lấy thông tin từ điển sau khi trả lời
  getInfo(currentQuestion.value.question);
  // Bắt đầu đếm ngược cho câu tiếp theo
  startNextQuestionCountdown();
};

const getOptionClass = (option: string) => {
  if (!selectedAnswer.value && !isTimeUp.value)
    return "bg-white hover:bg-gray-100";

  const isCorrect = option === currentQuestion.value.correctAnswer;
  const isSelected = option === selectedAnswer.value;

  if (isTimeUp.value && isCorrect)
    return "bg-green-100 border-green-400 text-green-700";
  if (isSelected && isCorrect)
    return "bg-green-100 border-green-400 text-green-700";
  if (isSelected && !isCorrect) return "bg-red-100 border-red-400 text-red-700";
  if (isCorrect) return "bg-green-100 border-green-400 text-green-700";

  return "bg-white";
};

const goToNextQuestion = () => {
  stopNextQuestionCountdown();
  if (!isLastQuestion.value) {
    currentIndex.value++;
    selectedAnswer.value = null;
    isTimeUp.value = false;
    timeLeft.value = 10;
    startTimer();

    // Kiểm tra nếu đã trả lời đủ 5 câu
    if ((currentIndex.value + 1) % 5 === 0) {
      startBreak();
    }
  } else {
    alert(
      `${i18n.global.t("VOCABULARY_SECTION.FINISH_PRACTICE")}: ${score.value}/${
        questions.value.length
      }`
    );
  }
};

const startTimer = () => {
  stopTimer();
  timer.value = setInterval(() => {
    if (timeLeft.value > 0) {
      timeLeft.value--;
    } else {
      isTimeUp.value = true;
      stopTimer();
    }
  }, 1000);
};

const stopTimer = () => {
  if (timer.value) clearInterval(timer.value);
};

const startNextQuestionCountdown = () => {
  nextQuestionCountdown.value = 10;
  nextQuestionTimer.value = setInterval(() => {
    if (nextQuestionCountdown.value > 0) {
      nextQuestionCountdown.value--;
    } else {
      goToNextQuestion();
    }
  }, 1000);
};

const stopNextQuestionCountdown = () => {
  if (nextQuestionTimer.value) {
    clearInterval(nextQuestionTimer.value);
    nextQuestionTimer.value = null;
  }
};

const startBreak = () => {
  isBreakTime.value = true;
  breakTimeLeft.value = 5;
  breakTimer.value = setInterval(() => {
    if (breakTimeLeft.value > 0) {
      breakTimeLeft.value--;
    } else {
      endBreak();
    }
  }, 1000);
};

const endBreak = () => {
  if (breakTimer.value) {
    clearInterval(breakTimer.value);
    breakTimer.value = null;
  }
  isBreakTime.value = false;
  goToNextQuestion();
};

onMounted(() => {
  if (questions.value.length) {
    startTimer();
  }
});

onUnmounted(() => {
  stopTimer();
  stopNextQuestionCountdown();
});

return (_ctx: any,_cache: any) => {
  const _component_a_card = _resolveComponent("a-card")!

  return (_openBlock(), _createBlock(LearnEnglishPagePatternLayout, {
    title: _unref(i18n).global.t('VOCABULARY_SECTION.PRACTICE_VOCABULARY_LABEL')
  }, {
    content: _withCtx(() => [
      _createVNode(_Transition, {
        name: "fade",
        mode: "out-in"
      }, {
        default: _withCtx(() => [
          (isBreakTime.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createElementVNode("div", _hoisted_2, [
                  _createElementVNode("h2", _hoisted_3, _toDisplayString(_unref(i18n).global.t("VOCABULARY_SECTION.BREAK_TIME_TITLE")), 1),
                  _createElementVNode("p", _hoisted_4, _toDisplayString(_unref(i18n).global.t("VOCABULARY_SECTION.BREAK_TIME_DESCRIPTION")), 1),
                  _createElementVNode("div", _hoisted_5, _toDisplayString(breakTimeLeft.value) + "s ", 1)
                ])
              ]))
            : (questions.value.length && currentQuestion.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                  _createElementVNode("div", _hoisted_7, [
                    _createElementVNode("div", _hoisted_8, [
                      _createElementVNode("p", _hoisted_9, "Score : " + _toDisplayString(score.value), 1),
                      _createElementVNode("div", _hoisted_10, [
                        (_openBlock(), _createElementBlock("svg", _hoisted_11, [
                          _cache[0] || (_cache[0] = _createElementVNode("circle", {
                            class: "text-gray-200",
                            "stroke-width": "4",
                            stroke: "currentColor",
                            fill: "transparent",
                            r: "20",
                            cx: "24",
                            cy: "24"
                          }, null, -1)),
                          _createElementVNode("circle", {
                            class: "text-red-500",
                            "stroke-width": "4",
                            "stroke-dasharray": "125.6",
                            "stroke-dashoffset": 125.6 - (timeLeft.value / 10) * 125.6,
                            "stroke-linecap": "round",
                            stroke: "currentColor",
                            fill: "transparent",
                            r: "20",
                            cx: "24",
                            cy: "24"
                          }, null, 8, _hoisted_12)
                        ])),
                        _createElementVNode("span", _hoisted_13, _toDisplayString(timeLeft.value), 1)
                      ])
                    ]),
                    _createElementVNode("div", _hoisted_14, _toDisplayString(_unref(upperCase)(currentQuestion.value.question)), 1),
                    _createElementVNode("div", _hoisted_15, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(currentQuestion.value.options, (option) => {
                        return (_openBlock(), _createElementBlock("button", {
                          key: option,
                          class: _normalizeClass(["w-full text-left px-4 py-3 rounded-xl border bg-white shadow hover:shadow-md transition-all duration-300", getOptionClass(option)]),
                          disabled: !!selectedAnswer.value || isTimeUp.value,
                          onClick: ($event: any) => (selectAnswer(option))
                        }, _toDisplayString(option), 11, _hoisted_16))
                      }), 128))
                    ]),
                    _createElementVNode("div", _hoisted_17, [
                      _createElementVNode("div", _hoisted_18, _toDisplayString(currentIndex.value + 1) + " / " + _toDisplayString(questions.value.length), 1),
                      _createElementVNode("div", _hoisted_19, [
                        _createElementVNode("div", {
                          class: "h-full bg-gradient-to-r from-gray-400 to-blue-600 transition-all duration-500 ease-in-out",
                          style: _normalizeStyle({
                    width: `${((currentIndex.value + 1) / questions.value.length) * 100}%`,
                  })
                        }, null, 4)
                      ])
                    ]),
                    ((selectedAnswer.value || isTimeUp.value) && !isLoading.value)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
                          _createElementVNode("button", {
                            class: "bg-gradient-to-br from-blue-600 to-gray-600 text-white shadow-md p-2 rounded-md",
                            onClick: goToNextQuestion
                          }, _toDisplayString(isLastQuestion.value
                    ? _unref(i18n).global.t("VOCABULARY_SECTION.END_PRACTICE")
                    : _unref(i18n).global.t("VOCABULARY_SECTION.CONTINUE_PRACTICE")), 1)
                        ]))
                      : _createCommentVNode("", true),
                    (selectedAnswer.value || isTimeUp.value)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_21, [
                          (isLoading.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_22, [
                                _createVNode(_component_a_card, {
                                  loading: isLoading.value,
                                  hoverable: false,
                                  class: "border-none"
                                }, null, 8, ["loading"])
                              ]))
                            : (dictionaryData.value)
                              ? (_openBlock(), _createElementBlock("div", _hoisted_23, [
                                  _createElementVNode("div", _hoisted_24, [
                                    _createElementVNode("div", _hoisted_25, [
                                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(dictionaryData.value.phonetics, (phonetic, index) => {
                                        return (_openBlock(), _createElementBlock("div", {
                                          key: index,
                                          class: "flex items-center space-x-2 bg-white p-2 rounded-md shadow-sm"
                                        }, [
                                          (phonetic.text)
                                            ? (_openBlock(), _createElementBlock("span", _hoisted_26, _toDisplayString(phonetic.text), 1))
                                            : _createCommentVNode("", true),
                                          (phonetic.audio)
                                            ? (_openBlock(), _createElementBlock("button", {
                                                key: 1,
                                                onClick: ($event: any) => (playAudio(phonetic.audio)),
                                                class: "p-2 bg-blue-100 rounded-full hover:bg-blue-200 transition-colors"
                                              }, [
                                                _createVNode(_unref(SoundOutlined), { class: "text-blue-600 flex items-center" })
                                              ], 8, _hoisted_27))
                                            : _createCommentVNode("", true)
                                        ]))
                                      }), 128))
                                    ])
                                  ]),
                                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(dictionaryData.value.meanings, (meaning, index) => {
                                    return (_openBlock(), _createElementBlock("div", {
                                      key: index,
                                      class: "p-2 rounded-lg"
                                    }, [
                                      _createElementVNode("h3", _hoisted_28, _toDisplayString(meaning.partOfSpeech.toUpperCase()), 1),
                                      _createElementVNode("div", _hoisted_29, [
                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(meaning.definitions, (def, defIndex) => {
                                          return (_openBlock(), _createElementBlock("div", {
                                            key: defIndex,
                                            class: "p-2 rounded-md shadow-sm"
                                          }, [
                                            _createElementVNode("p", _hoisted_30, _toDisplayString(def.definition), 1),
                                            (def.example)
                                              ? (_openBlock(), _createElementBlock("p", _hoisted_31, _toDisplayString(_unref(i18n).global.t("dictionary.example")) + " : " + _toDisplayString(def.example), 1))
                                              : _createCommentVNode("", true)
                                          ]))
                                        }), 128))
                                      ])
                                    ]))
                                  }), 128))
                                ]))
                              : _createCommentVNode("", true)
                        ]))
                      : _createCommentVNode("", true)
                  ])
                ]))
              : (_openBlock(), _createElementBlock("div", _hoisted_32, _toDisplayString(_unref(i18n).global.t("VOCABULARY_SECTION.NO_DATA")), 1))
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["title"]))
}
}

})